import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  deleteFeatureRelation,
  selectCurrentRoleId,
  selectNamesData,
  setActivateButtonDisable,
} from "../../store/slices/CreateRoleSlice";
import RoleSetupPanleComponent from "../CreateEditRoleCommonFolder/RoleSetupPanelComponent";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import CreateEditRoleFEatureSidePanel from "./CreateEditRoleFeatureSidePanel";
import { triggerToast } from "../../utils/toast";
import GridTableCommon from "../GridTableCommon/GridTableCommon";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { getDataForTable } from "../../services/RequestAccess/RequestAccess";
import { formatFilterValue } from "../../utils/formatFilters";
import * as style from "./CreateEditRoleFeatureSidePanel.module.scss";
import { useIdToken } from "../../hooks/useAuthSession";

interface CreateEditRoleFeatureProps {
  isEditable?: boolean;
}

const CreateEditRoleFeature: React.FC<CreateEditRoleFeatureProps> = ({
  isEditable = false,
}) => {
  const scssClass = style as any;

  const [refreshValue, setRefreshValue] = useState<number>(0);
  const idToken = useIdToken();
  const currentRoleId = useSelector(selectCurrentRoleId);
  const dispatch = useDispatch<AppDispatch>();
  const [openEditPanel, setOpenEditPanel] = useState(false);
  const [selectedFeatureRow, setSelectedFeatureRow] = useState({});
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [featureRelationsSubmitBtn, setFeatureRelationsSubmitBtn] =
    useState(true);

  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gridData, setGridData] = useState<GridTableDataType | any>({
    rows: [],
    totalCount: 0,
  });

  const featureRelationsStatus = useSelector(
    (state: RootState) => state.createRole.featureRelations,
  );
  const createFeaturePayload = useSelector(
    (state: RootState) => state.createRole.featureRelations.createEditPayload,
  );
  const namesData = useSelector(selectNamesData);
  // Associated Groups column is a CRM-only feature; other applications keep
  // the pre-existing grid (no Associated Groups column).
  const isCrmApp = useSelector(
    (state: RootState) => state.applicationDetails?.data?.app_cmdb_id === "CRM",
  );

  useEffect(() => {
    if (featureRelationsStatus?.status === "succeeded") {
      setRefreshValue((prev) => prev + 1);
    }
    if (namesData?.status === "active") {
      dispatch(setActivateButtonDisable(true));
    } else if (gridData?.rows?.length > 0) {
      dispatch(setActivateButtonDisable(false));
    } else {
      dispatch(setActivateButtonDisable(true));
    }
  }, [featureRelationsStatus, gridData]);

  useEffect(() => {
    const hasIncompleteEnvCondition =
      createFeaturePayload?.associatedGroup &&
      !Array.isArray(createFeaturePayload.associatedGroup) &&
      Object.values(createFeaturePayload.associatedGroup).some(
        (values: any) => Array.isArray(values) && values.length === 0,
      );
    if (
      createFeaturePayload?.featureId !== null &&
      createFeaturePayload?.permissionIds?.length > 0 &&
      createFeaturePayload?.permissionIds?.some(
        (permission: { permissionName: string }) =>
          permission.permissionName === "view",
      ) &&
      !hasIncompleteEnvCondition
    ) {
      setFeatureRelationsSubmitBtn(false);
    } else {
      setFeatureRelationsSubmitBtn(true);
    }
  }, [createFeaturePayload]);

  const handleRoleEditFeature = (event: any, row: any) => {
    setOpenEditPanel(true);
    setSelectedFeatureRow(row.original);
  };

  const handleClose = () => setOpenEditPanel(false);

  const handleDeleteFeatureRelation = async (rows: any) => {
    try {
      const deleteFeaturePayload: any = rows.map((row: any) => ({
        roleId: currentRoleId,
        featureId: row?.feature_id,
        associatedGroup: row?.associated_groups ?? [],
      }));
      const res = await dispatch(deleteFeatureRelation(deleteFeaturePayload));
      if (res?.payload?.status === "SUCCESS") {
        triggerToast(`Feature Relations deleted successfully`, "success");
        setRefreshValue((prev) => prev + 1);
        setSelectedRows([]);
        setSelectedRowsData([]);
      } else if (res?.error) {
        triggerToast(res?.error?.message || res?.payload?.message, "error");
      }
    } catch (error) {
      triggerToast(`Failed to delete Feature Relations`, "error");
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await getDataForTable(
      `/rolesetup/role-feature/${currentRoleId}`,
      {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? (filters || [])?.map((item: any) =>
                  formatFilterValue(item?.value, item?.id),
                )
              : [],
          sort: sorting?.map((item: any) => {
            return { columnName: item?.id, type: item?.desc ? "DESC" : "ASC" };
          }),
          page: {
            limit: pagination?.rowsPerPage,
            offset: pagination?.page,
          },
        },
      },
    );
    resp.data.rows = resp?.data?.rows?.map((row: any) => {
      return {
        ...row,
        action: row?.action?.map((action: any) => {
          return {
            ...action,
            disabled: isEditable
              ? action?.disabled
              : action?.disabled || action?.value === "Edit",
          };
        }),
      };
    });
    if (!isCrmApp) {
      resp.data.columns = resp?.data?.columns?.filter(
        (col: any) =>
          !["id", "accessorKey", "name", "header"]
            .map((key) => col?.[key]?.toString()?.toLowerCase())
            .some((value) => value?.includes("associated_app_group") ||
              value === "associated groups"),
      );
    }
    if (JSON.stringify(resp?.data) !== JSON.stringify(gridData)) {
      setGridData(resp?.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [sorting, pagination, refreshValue]);
  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <>
      <div className={scssClass.tableContainer}>
        {idToken && (
          <GridTableCommon
            gridData={gridData ?? {}}
            selectedRows={selectedRows}
            selectedData={selectedRowsData}
            setSelectedData={setSelectedRowsData}
            refresh={refreshValue}
            onSelectionChange={setSelectedRows}
            style={{
              height: "calc(100vh - 38rem)",
              maxWidth: "calc(100vw - 4rem)",
            }}
            keyId="feature_id"
            title="Feature Details"
            loading={isLoading}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            settings={(data: any) => {
              if (namesData?.status === "active") {
                dispatch(setActivateButtonDisable(true));
              } else if (data?.rows?.length !== 0) {
                dispatch(setActivateButtonDisable(false));
              } else {
                dispatch(setActivateButtonDisable(true));
              }
            }}
            cellActions={[
              {
                event: "Edit",
                disabled: !isEditable,
                callback: (_event: any, row: any) => {
                  handleRoleEditFeature(event, row.row);
                },
                icon: true,
              },
            ]}
            rowActions={[
              {
                event: "Delete",
                callback: (_event: any, rows: any) => {
                  handleDeleteFeatureRelation(selectedRowsData);
                },
              },
            ]}
          />
        )}
      </div>
      {openEditPanel && (
        <RoleSetupPanleComponent
          open={openEditPanel}
          title="Edit Feature Mapping"
          isEdit={true}
          onClose={handleClose}
          BodyComponent={
            <CreateEditRoleFEatureSidePanel
              isEdit={true}
              selectedRow={selectedFeatureRow}
            />
          }
          tabValue={1}
          isSubmitDisable={featureRelationsSubmitBtn}
        />
      )}
    </>
  );
};

export default CreateEditRoleFeature;
